/**
 * BYOD dashboard builder canvas — orchestrates drag-and-drop, row layout,
 * and the empty state. The DnD architecture lives across several files so
 * each can be reasoned about in isolation:
 *
 *   - `dnd-config.ts`        — DRAG_TYPE constants + sensor configuration.
 *   - `sortable-tile.tsx`    — single tile wrapped in `useSortable`.
 *   - `row-end-drop-zone.tsx`— sentinel droppable filling row tail width.
 *   - `droppable-tile-row.tsx`— row wrapper with capacity / mismatch logic.
 *   - `inter-row-divider-stack.tsx` — gap between rows; idle = "+" buttons,
 *     dragging = matching new-row drop band.
 *   - `hooks/use-drag-indicator.ts` — single-source-of-truth indicator.
 *   - `hooks/use-byod-keyboard-nav.ts` — row-aware keyboard arrow nav.
 *   - `hooks/use-accessibility-plugin.ts` — screen-reader announcements.
 *   - `utils/build-drag-end-result.ts` — `@dnd-kit` event → DragEndResult.
 *   - `utils/handle-drag-end.ts` — pure drop-decision function.
 *
 * Canvas orchestrates: it owns the `DragDropProvider`, the
 * activeDrag/hoveredInvalid state, and the store mutations triggered by
 * the decision function.
 */
import * as React from 'react';

import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {DraggableWrapper} from '@spaced-out/ui-design-system/lib/components/DraggableWrapper';
import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {DragDropProvider, DragOverlay} from '@dnd-kit/react';
import type {DragStartEvent, DragEndEvent} from '@dnd-kit/react';
import {Accessibility} from '@dnd-kit/dom';

import {classify} from 'src/utils/classify';
import logger from 'src/utils/logger';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useAgencyData} from 'src/hooks/useAgencyData';
import {useHistory} from 'src/rerouter';
import {useShallow} from 'zustand/react/shallow';
import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {
  trackReportRemovedFromDashboard,
  trackDashboardLayoutChanged,
  trackDashboardSectionAdded,
} from 'src/components/analytics-genesis/byod/analytics/byod-amplitude-track';
import {MAX_TILES_PER_ROW} from 'src/components/analytics-genesis/byod/constants';
import {ROW_TYPE} from 'src/types/byod';
import type {Row} from 'src/types/byod';

import {ByodDragOverlayProvider} from 'src/components/analytics-genesis/byod/api/queries/useTileData';
import {CanvasTile} from './canvas-tile';
import {SortableTile} from './sortable-tile';
import {SectionRow} from './section-row';
import {TileDivider} from './tile-divider';
import {TilePlaceholder} from './tile-placeholder';
import {RowEndDropZone} from './row-end-drop-zone';
import {DroppableTileRow} from './droppable-tile-row';
import {InterRowDividerStack} from './inter-row-divider-stack';
import {
  BYOD_SENSORS,
  DRAG_TYPE,
  dropTypeForRow,
  type DragType,
} from './dnd-config';
import {useDragIndicator} from '../hooks/use-drag-indicator';
import {useByodKeyboardNav} from '../hooks/use-byod-keyboard-nav';
import {useAccessibilityPlugin} from '../hooks/use-accessibility-plugin';
import {useExitAnimation} from '../hooks/use-exit-animation';
import type {InvalidHoverReason} from './tile-row';
import {NEW_ROW_PREFIX, decideDragOutcome} from '../utils/handle-drag-end';
import {buildDragEndResult} from '../utils/build-drag-end-result';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type {InvalidHoverReason};

export type CanvasProps = {
  pathname: string;
  canEdit: boolean;
};

// ---------------------------------------------------------------------------
// ByodKeyboardNavBridge — `useByodKeyboardNav` reads from the
// `DragDropProvider` context. Canvas itself is *outside* that provider (it
// renders the provider in its JSX), so the hook would resolve a null
// manager and silently no-op if called at Canvas level. Mounting this
// component as a child of `DragDropProvider` runs the hook inside the
// context where the manager is resolvable.
// ---------------------------------------------------------------------------

function ByodKeyboardNavBridge() {
  useByodKeyboardNav();
  return null;
}

// ---------------------------------------------------------------------------
// rowClass — map a row's `type` to the correct flex layout class.
// ---------------------------------------------------------------------------

function rowClass(type: string): string {
  if (type === ROW_TYPE.KPI) {
    return layoutCss.canvasRowKpi;
  }
  if (type === ROW_TYPE.VIZ) {
    return layoutCss.canvasRowViz;
  }
  return layoutCss.canvasRow;
}

// ---------------------------------------------------------------------------
// ExitingRowPlaceholder — aria-hidden div that runs the row-collapse
// keyframe animation in place of a row that just got removed from the
// store. Used twice (mid-list and trailing).
// ---------------------------------------------------------------------------

type ExitingRow = ReturnType<typeof useExitAnimation>[number];

function ExitingRowPlaceholder({row}: {row: ExitingRow}) {
  return (
    <div
      className={classify(
        layoutCss.canvasRowExiting,
        row.rowType === ROW_TYPE.KPI && layoutCss.canvasRowExitingKpi,
        row.rowType === ROW_TYPE.VIZ && layoutCss.canvasRowExitingViz,
        row.rowType === ROW_TYPE.SECTION && layoutCss.canvasRowExitingSection,
      )}
      aria-hidden
    />
  );
}

// ---------------------------------------------------------------------------
// Canvas — main orchestrator
// ---------------------------------------------------------------------------

export const Canvas = React.memo(({pathname, canEdit}: CanvasProps) => {
  const {t} = useLabelTranslation();
  const history = useHistory();
  const {id: agencyId, name: agencyName} = useAgencyData();
  const rowOrder = useDashboardStore((s) => s.rowOrder);
  const rows = useDashboardStore((s) => s.rows);
  // Narrow selector: only extract widget labels (the sole field Canvas reads).
  // useShallow prevents re-renders when non-label mutations occur (filter
  // changes, config edits, chart style toggles).
  const widgetLabels = useDashboardStore(
    useShallow((s) => {
      const labels: Record<string, string> = {};
      for (const [id, w] of Object.entries(s.widgets)) {
        labels[id] = w?.label ?? id;
      }
      return labels;
    }),
  );
  const storeRemoveWidget = useDashboardStore((s) => s.removeWidget);
  const addSectionRow = useDashboardStore((s) => s.addSectionRow);
  const updateRowLabel = useDashboardStore((s) => s.updateRowLabel);
  const removeSectionRow = useDashboardStore((s) => s.removeSectionRow);
  const moveTile = useDashboardStore((s) => s.moveTile);
  const moveTileToNewRow = useDashboardStore((s) => s.moveTileToNewRow);
  const focusTileId = useDashboardStore((s) => s.focusTileId);
  const setFocusTile = useDashboardStore((s) => s.setFocusTile);

  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [dragState, setDragState] = React.useState<{
    type: DragType | null;
    id: string | null;
    rowType: string | null;
    width: number | null;
  }>({type: null, id: null, rowType: null, width: null});
  const activeDragType = dragState.type;
  const activeDragId = dragState.id;
  const activeDragRowType = dragState.rowType;
  const activeDragWidth = dragState.width;
  const [hoveredInvalid, setHoveredInvalid] = React.useState<{
    rowId: string;
    reason: InvalidHoverReason;
  } | null>(null);
  // Mirror in a ref so onDragEnd reads the latest hover at drop time
  // without having to rebuild the callback on every hover change.
  const hoveredInvalidRef = React.useRef(hoveredInvalid);
  hoveredInvalidRef.current = hoveredInvalid;

  const handleInvalidHover = React.useCallback(
    (rowId: string | null, reason: InvalidHoverReason | null) => {
      setHoveredInvalid(rowId && reason ? {rowId, reason} : null);
    },
    [],
  );

  const showRejectionToast = React.useCallback(
    (reason: InvalidHoverReason) => {
      let title: string;
      if (reason.kind === 'type-mismatch') {
        title =
          reason.rowType === ROW_TYPE.KPI
            ? t(
                'Only_KPI_tiles_can_be_placed_in_KPI_rows',
                'Only KPI tiles can be placed in KPI rows.',
              )
            : t(
                'Only_charts_and_tables_can_be_placed_in_this_row',
                'Only charts and tables can be placed in this row.',
              );
      } else {
        title = t('This_row_is_full_Maximum_count_tiles_per_row', {
          count: reason.max,
          defaultValue: `This row is full. Maximum ${reason.max} tiles per row.`,
        });
      }
      toastApi.show(
        <Toast initialFocus={0} semantic="information">
          <ToastTitle>{title}</ToastTitle>
        </Toast>,
      );
    },
    [t],
  );

  // Programmatic scroll-to-tile (used after "edit" navigation returns).
  // requestAnimationFrame so the layout is committed before scrollIntoView.
  React.useEffect(() => {
    if (!focusTileId) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      setFocusTile(null);
      try {
        const escaped = CSS.escape(focusTileId);
        const el = document.querySelector<HTMLElement>(
          `[data-tile-id="${escaped}"]`,
        );
        if (el) {
          el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      } catch (e) {
        logger.warn('Failed to scroll to tile', {focusTileId, error: e});
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [focusTileId, setFocusTile]);

  const exitingRows = useExitAnimation(rowOrder, rows);

  // Pre-group exiting rows by their insertion index so the render loop
  // does a Map lookup instead of filtering the full array on every row.
  const exitingByInsertAt = React.useMemo(() => {
    const map = new Map<number, ExitingRow[]>();
    for (const e of exitingRows) {
      const list = map.get(e.insertAt);
      if (list) {
        list.push(e);
      } else {
        map.set(e.insertAt, [e]);
      }
    }
    return map;
  }, [exitingRows]);

  const handleAddSection = React.useCallback(
    (afterIndex: number) => {
      addSectionRow(afterIndex, t('New_Section', 'New Section'));
      trackDashboardSectionAdded({
        agency_id: agencyId,
        agency_name: agencyName,
        dashboardId: useDashboardStore.getState().id,
        dashboardName: useDashboardStore.getState().name,
      });
      const {rowOrder: newOrder} = useDashboardStore.getState();
      const newRowIndex = afterIndex < 0 ? 0 : afterIndex + 1;
      const newRowId = newOrder[newRowIndex];
      if (newRowId) {
        setEditingRowId(newRowId);
      }
    },
    [addSectionRow, t, agencyId, agencyName],
  );

  const handleEdit = React.useCallback(
    (tileId: string) => {
      history.push(`${pathname}/widget/edit/${tileId}`);
    },
    [history, pathname],
  );

  const handleRemove = React.useCallback(
    (tileId: string) => {
      const widget = useDashboardStore.getState().widgets[tileId];
      trackReportRemovedFromDashboard({
        agency_id: agencyId,
        agency_name: agencyName,
        dashboardId: useDashboardStore.getState().id,
        dashboardName: useDashboardStore.getState().name,
        reportName: widget?.label ?? tileId,
      });
      storeRemoveWidget(tileId);
    },
    [storeRemoveWidget, agencyId, agencyName],
  );

  // Translate a droppable id (row id, new-row band id) into a human-readable
  // position label for the screen-reader announcements.
  const describeDroppable = React.useCallback(
    (droppableId: string): string => {
      if (droppableId.startsWith(NEW_ROW_PREFIX)) {
        const variant = droppableId.split('-')[2];
        return variant === 'kpi'
          ? t('new_KPI_row', 'new KPI row')
          : t('new_chart_or_table_row', 'new chart or table row');
      }
      const idx = rowOrder.indexOf(droppableId);
      const row = rows[droppableId];
      if (!row || idx === -1) {
        return t('row', 'row');
      }
      const position = idx + 1;
      if (row.type === ROW_TYPE.KPI) {
        return t('KPI_row_position', {
          position,
          defaultValue: `KPI row ${position}`,
        });
      }
      if (row.type === ROW_TYPE.VIZ) {
        return t('chart_and_table_row_position', {
          position,
          defaultValue: `chart and table row ${position}`,
        });
      }
      return row.label || t('section_row', 'section row');
    },
    [rowOrder, rows, t],
  );

  const accessibilityPlugin = useAccessibilityPlugin(describeDroppable, t);

  const onDragStart = React.useCallback((event: DragStartEvent) => {
    const src = event.operation.source;
    if (!src) {
      return;
    }
    const rawData = src.data;
    const srcData =
      rawData != null && typeof rawData === 'object'
        ? (rawData as {type?: string; rowId?: string})
        : undefined;
    const dtype: DragType | null =
      srcData?.type === DRAG_TYPE.KPI || srcData?.type === DRAG_TYPE.VIZ
        ? srcData.type
        : null;

    let rowType: string | null = null;
    if (srcData?.rowId) {
      const row = useDashboardStore.getState().rows[srcData.rowId];
      rowType = row?.type ?? null;
    }

    // Capture the source tile's rendered width so the DragOverlay clone
    // matches it (instead of stretching to the overlay's container width).
    const srcEl = src.element instanceof HTMLElement ? src.element : null;
    const width = srcEl ? srcEl.getBoundingClientRect().width : null;

    setDragState({type: dtype, id: String(src.id), rowType, width});
    setHoveredInvalid(null);
  }, []);

  const onDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const invalidAtDrop = hoveredInvalidRef.current;
      setDragState({type: null, id: null, rowType: null, width: null});
      setHoveredInvalid(null);

      const result = buildDragEndResult(event);
      if (!result) {
        return;
      }
      const outcome = decideDragOutcome(result, invalidAtDrop);

      switch (outcome.kind) {
        case 'rejected':
          showRejectionToast(outcome.reason);
          return;
        case 'cancelled':
        case 'returned':
        case 'same-position':
        case 'no-op':
          return;
        case 'move-to-new-row':
          moveTileToNewRow(
            outcome.fromRowId,
            outcome.fromIndex,
            outcome.afterIndex,
            outcome.newRowType,
          );
          trackDashboardLayoutChanged({
            agency_id: agencyId,
            agency_name: agencyName,
            dashboardId: useDashboardStore.getState().id,
            dashboardName: useDashboardStore.getState().name,
          });
          return;
        case 'move-tile':
          moveTile(
            outcome.fromRowId,
            outcome.toRowId,
            outcome.fromIndex,
            outcome.toIndex,
          );
          trackDashboardLayoutChanged({
            agency_id: agencyId,
            agency_name: agencyName,
            dashboardId: useDashboardStore.getState().id,
            dashboardName: useDashboardStore.getState().name,
          });
          return;
      }
    },
    [moveTile, moveTileToNewRow, showRejectionToast, agencyId, agencyName],
  );

  const {
    indicatorTarget,
    source: dragSource,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  } = useDragIndicator({
    onDragStart,
    onDragOver: undefined,
    onDragEnd,
  });

  const getWrapperClass = React.useCallback(
    (rowType: string) => {
      const tileTypeClass =
        rowType === ROW_TYPE.KPI
          ? layoutCss.tileDraggableFillKpi
          : layoutCss.tileDraggableFillViz;
      return canEdit
        ? classify(
            layoutCss.tileDraggableFill,
            tileTypeClass,
            layoutCss.tileDraggableFillEdit,
          )
        : classify(
            layoutCss.tileDraggableFill,
            tileTypeClass,
            layoutCss.tileDraggableFillView,
          );
    },
    [canEdit],
  );

  const plugins = React.useCallback(
    (defaults: any[]) => [
      ...defaults.filter(
        (p: any) => (typeof p === 'function' ? p : p.plugin) !== Accessibility,
      ),
      accessibilityPlugin,
    ],
    [accessibilityPlugin],
  );

  if (rowOrder.length === 0) {
    return (
      <div className={layoutCss.emptyCanvas} data-testid="byod-canvas-empty">
        <EmptyState
          title={t('No_widgets_yet', 'No widgets yet')}
          imageVariant="data"
        >
          {canEdit && (
            <Button
              type="primary"
              size="medium"
              iconLeftName="plus"
              iconLeftType="regular"
              onClick={() => history.push(`${pathname}/widget/add`)}
              testId="byod-canvas-empty-add-report"
            >
              {t('Add_report', 'Add report')}
            </Button>
          )}
        </EmptyState>
      </div>
    );
  }

  const renderTilesAndExtras = ({
    row,
    rowId,
    maxTiles,
    isRowAtCapacity,
    isSourceInThisRow,
    placeholderIndex,
  }: {
    row: Row;
    rowId: string;
    maxTiles: number;
    isRowAtCapacity: boolean;
    isSourceInThisRow: boolean;
    placeholderIndex: number | null;
  }) => {
    const wrapperClass = getWrapperClass(row.type);
    const dropType = dropTypeForRow(row.type);
    // During a drag, we drop the inter-tile TileDivider/tileGap DOM
    // elements entirely and rely on the row's CSS column-gap (set under
    // [data-dragging='true']). The optimistic-sort plugin reorders sortable
    // children only — non-sortable gap/divider siblings stay at their
    // original DOM index, which leaves the visual gap stuck between the
    // wrong pair of tiles. CSS gap travels with whichever items are
    // currently in the row.
    const isDragging = activeDragType !== null;
    return (
      <>
        {row.tiles.map((tileId, colIndex) => {
          const showEndDivider =
            !isDragging &&
            canEdit &&
            row.type !== ROW_TYPE.KPI &&
            colIndex === row.tiles.length - 1 &&
            row.tiles.length < maxTiles;

          return (
            <React.Fragment key={tileId}>
              {placeholderIndex === colIndex && (
                <TilePlaceholder rowType={row.type} />
              )}
              <SortableTile
                tileId={tileId}
                colIndex={colIndex}
                canEdit={canEdit}
                dropType={dropType}
                rowId={rowId}
                wrapperClass={wrapperClass}
                label={widgetLabels[tileId] ?? tileId}
                disableAsTarget={isRowAtCapacity}
                onEdit={handleEdit}
                onRemove={handleRemove}
              />
              {!isDragging &&
                colIndex < row.tiles.length - 1 &&
                (canEdit ? (
                  <TileDivider
                    rowId={rowId}
                    rowType={row.type}
                    colIndex={colIndex + 1}
                    pathname={pathname}
                    interactive={row.tiles.length < maxTiles}
                  />
                ) : (
                  <div className={layoutCss.tileGap} />
                ))}
              {showEndDivider && (
                <TileDivider
                  rowId={rowId}
                  rowType={row.type}
                  colIndex={row.tiles.length}
                  pathname={pathname}
                  interactive
                />
              )}
            </React.Fragment>
          );
        })}
        {placeholderIndex !== null && placeholderIndex >= row.tiles.length && (
          <TilePlaceholder rowType={row.type} />
        )}
        <RowEndDropZone
          rowId={rowId}
          dropType={dropType}
          activeDragType={activeDragType}
          isAtCapacity={isRowAtCapacity}
          isSourceInThisRow={isSourceInThisRow}
        />
      </>
    );
  };

  const renderKpiAddButton = (rowId: string, tilesLength: number) => (
    <>
      <div className={layoutCss.tileGap} />
      <button
        type="button"
        className={layoutCss.kpiPlaceholder}
        aria-label={t('Add_KPI', 'Add KPI')}
        onClick={() =>
          history.push(
            `${pathname}/widget/add?rowId=${rowId}&col=${tilesLength}&rowType=kpi`,
          )
        }
      >
        <Icon name="plus" size="medium" color="clickable" />
      </button>
    </>
  );

  const renderTilesRow = (row: Row, rowId: string) => {
    const maxTiles = MAX_TILES_PER_ROW[row.type] ?? Infinity;
    const dropType = dropTypeForRow(row.type);
    const sourceRowId = dragSource?.droppableId ?? null;
    const isSourceInThisRow = sourceRowId === rowId && activeDragType !== null;
    const isRowAtCapacity =
      activeDragType !== null &&
      activeDragType === dropType &&
      row.tiles.length >= maxTiles &&
      sourceRowId !== rowId;
    const showKpiAdd =
      canEdit &&
      row.type === ROW_TYPE.KPI &&
      row.tiles.length < maxTiles &&
      activeDragType === null;

    // Cross-row drop placeholder: only render the tile-sized "make room"
    // slot when hovering over a different row than the source. Same-row
    // reorders fall back to just the vertical indicator line — adding a
    // placeholder there would inflate the row past its tile-count cap
    // (e.g. a 5th visible slot in a max-4 KPI row).
    const isCrossRowHover =
      indicatorTarget !== null &&
      indicatorTarget.droppableId === rowId &&
      indicatorTarget.orientation === 'vertical' &&
      !isSourceInThisRow &&
      !isRowAtCapacity;
    const placeholderIndex = isCrossRowHover
      ? Math.min(indicatorTarget!.index, row.tiles.length)
      : null;

    return (
      <DroppableTileRow
        rowId={rowId}
        row={row}
        dropType={dropType}
        activeDragType={activeDragType}
        dragSourceRowId={sourceRowId}
        isRowAtCapacity={isRowAtCapacity}
        indicatorTarget={indicatorTarget}
        suppressInlineIndicator={placeholderIndex !== null}
        hoveredInvalid={hoveredInvalid}
        onInvalidHover={handleInvalidHover}
        rowClassName={rowClass(row.type)}
        tiles={renderTilesAndExtras({
          row,
          rowId,
          maxTiles,
          isRowAtCapacity,
          isSourceInThisRow,
          placeholderIndex,
        })}
        trailingContent={
          showKpiAdd ? renderKpiAddButton(rowId, row.tiles.length) : null
        }
      />
    );
  };

  const renderInterRow = (afterIndex: number) =>
    canEdit ? (
      <InterRowDividerStack
        afterIndex={afterIndex}
        pathname={pathname}
        activeDragType={activeDragType}
        indicatorTarget={indicatorTarget}
        onAddSection={handleAddSection}
      />
    ) : (
      <div className={layoutCss.rowGap} />
    );

  return (
    <DragDropProvider
      sensors={BYOD_SENSORS}
      plugins={plugins}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <ByodKeyboardNavBridge />
      <div className={layoutCss.layoutRows} data-testid="byod-canvas">
        {rowOrder.map((rowId, rowIndex) => {
          const row = rows[rowId];
          if (!row) {
            return null;
          }
          return (
            <React.Fragment key={rowId}>
              {(exitingByInsertAt.get(rowIndex) ?? []).map((e) => (
                <ExitingRowPlaceholder key={`exiting-${e.rowId}`} row={e} />
              ))}
              {renderInterRow(rowIndex - 1)}
              {row.type === ROW_TYPE.SECTION ? (
                <div className={rowClass(row.type)} data-row-type={row.type}>
                  <SectionRow
                    label={row.label ?? ''}
                    canEdit={canEdit}
                    isEditing={editingRowId === rowId}
                    onStartEdit={() => setEditingRowId(rowId)}
                    onSaveLabel={(label) => {
                      updateRowLabel(rowId, label);
                      setEditingRowId(null);
                    }}
                    onDelete={() => removeSectionRow(rowId)}
                  />
                </div>
              ) : (
                renderTilesRow(row, rowId)
              )}
            </React.Fragment>
          );
        })}
        {exitingRows
          .filter((e) => e.insertAt >= rowOrder.length)
          .map((e) => (
            <ExitingRowPlaceholder key={`exiting-${e.rowId}`} row={e} />
          ))}
        {renderInterRow(rowOrder.length - 1)}
      </div>
      {/*
        dropAnimation={null} — the default 250ms drop animation slides the
        overlay clone toward `source.element`. With OptimisticSortingPlugin
        disabled (see sortable-tile.tsx, plugins: []), the source element
        stays at its ORIGINAL slot for the entire drag, so the animation
        slides the clone back to where the user picked it up — even on a
        successful cross-row drop. That reads as "snap back / drop failed"
        when the drop actually succeeded. Disabling is the cleaner UX:
        clone disappears, real tile re-renders at its new position.
      */}
      <DragOverlay dropAnimation={null}>
        {activeDragId && activeDragId in widgetLabels ? (
          <ByodDragOverlayProvider value={true}>
            <DraggableWrapper
              enableNativeDrag={false}
              isDragging={false}
              wrapperProps={
                activeDragWidth
                  ? {style: {width: `${activeDragWidth}px`}}
                  : undefined
              }
              classNames={{
                wrapper: classify(
                  getWrapperClass(activeDragRowType ?? ROW_TYPE.VIZ),
                  layoutCss.tileDraggableFillDragging,
                ),
                dragHandle: layoutCss.tileDraggableHandleHidden,
              }}
            >
              <CanvasTile
                tileId={activeDragId}
                canEdit={false}
                isRemoving={false}
                onEdit={handleEdit}
                onRemove={handleRemove}
              />
            </DraggableWrapper>
          </ByodDragOverlayProvider>
        ) : null}
      </DragOverlay>
    </DragDropProvider>
  );
});
